import React from "react";
import {IFrameModalMessageIds} from "@emburse/ui-complex";
import {Alert, Box, Button, ButtonContainer, ModalFooter, ModalHeader, Snackbar} from "@emburse/ui-core";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router";
import './FooterBar.scss';
import {BaseConfigurationContext} from "shared/context/BaseConfigurationContext";
import {useErpType} from "shared/hooks";
import {ISaveConfigurationResponse} from "shared/models";
import {isTrustedEmbarkMessageEvent, lodash} from "shared/utils";
import {ConfirmationModal} from "../ConfirmationModal/ConfirmationModal";

export interface IFooterBarConfigurationService {
    saveConfiguration: (configuration: any, id: string) => Promise<ISaveConfigurationResponse>;
    updateConfigurationStatus: (successfulSyncConfiguration: boolean, id: string) => Promise<boolean>;
}

export interface FooterBarProps {
    configurationService: IFooterBarConfigurationService;
    getConfigurationFromLocalStorage: (companyId: string, configurationId: string) => unknown;
    reviewRoute?: string;
}

export const FooterBar = ({
                              configurationService,
                              getConfigurationFromLocalStorage,
                              reviewRoute = '/review'
                          }: FooterBarProps) => {
    const {t} = useTranslation();
    const {
        configurationId,
        companyId,
        hasError,
        isInsideEmbark,
        basePath,
        successfulSyncConfiguration,
        setSuccessfulSyncConfiguration,
        initialConfiguration,
        updatedConfiguration
    } = React.useContext(BaseConfigurationContext);

    const erpType = useErpType() ?? 'netsuite';
    const navigate = useNavigate();
    const [open, setOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>(t('savingConfigurationErrorMessage'));
    const [reviewModalOpened, setReviewModalOpened] = React.useState(false);
    const [cancelModalOpened, setCancelModalOpened] = React.useState(false);
    const [showFooterButtons, setShowFooterButtons] = React.useState(true);

    const cancel = () => {
        const changeMade = !lodash.isEqual(initialConfiguration, localStorageConfiguration);
        if (changeMade) {
            setCancelModalOpened(true);
            setShowFooterButtons(false);
            if (isInsideEmbark) {
                window.parent.postMessage({message: IFrameModalMessageIds.headerShowClose, value: false}, '*');
            }
        } else {
            closeModal();
        }
    };

    const closeModal = () => {
        if (isInsideEmbark) {
            window.parent.postMessage({
                message: IFrameModalMessageIds.modalCallback,
                value: {action: IFrameModalMessageIds.modalCallBack_closeModal}
            }, '*');
        }
    };

    const localStorageConfiguration = getConfigurationFromLocalStorage(companyId, configurationId);

    const saveAndClose = () => {
        const changeMade = !lodash.isEqual(updatedConfiguration, localStorageConfiguration);
        if (changeMade) {
            setReviewModalOpened(true);
            setShowFooterButtons(false);
            if (isInsideEmbark) {
                window.parent.postMessage({message: IFrameModalMessageIds.headerShowClose, value: false}, '*');
            }
        } else {
            saveSettings(successfulSyncConfiguration ?? false);
        }
    };

    const saveSettings = (syncConfiguration: boolean) => {
        configurationService.saveConfiguration(localStorageConfiguration, configurationId).then(response => {
            if (response.success) {
                saveConfigurationStatus(syncConfiguration);
            } else {
                setOpen(true);
                if (response.expiredToken) {
                    setErrorMessage(t('tokenExpiredError'))
                }
            }
        });
    };

    const saveConfigurationStatus = (syncConfiguration: boolean) => {
        configurationService.updateConfigurationStatus(syncConfiguration, configurationId).then(response => {
            if (response) {
                closeModal();
            } else {
                setOpen(true);
            }
        });
    };

    const cancelRef = React.useRef<HTMLButtonElement>(null);
    const saveAndCloseRef = React.useRef<HTMLButtonElement>(null);

    const handleIFrameModalMessage = React.useCallback((event: MessageEvent) => {
        if (!isTrustedEmbarkMessageEvent(event)) {
            return;
        }
        if (event?.data?.message === IFrameModalMessageIds.footerButton_action) {
            if (event.data.value === 'cancel') cancelRef.current?.click();
            if (event.data.value === 'saveAndClose') saveAndCloseRef.current?.click();
        }
    }, []);

    React.useEffect(() => {
        window.addEventListener('message', handleIFrameModalMessage);
        return () => {
            window.removeEventListener('message', handleIFrameModalMessage);
        };
    }, [handleIFrameModalMessage]);

    React.useEffect(() => {
        if (showFooterButtons) {
            window.parent.postMessage({
                message: IFrameModalMessageIds.footerButtons, value: [
                    {
                        'id': "cancel",
                        'data-qa': `${erpType}-cancel-button`,
                        'variant': "outlined",
                        'children': t('cancel'),
                    },
                    {
                        'id': "saveAndClose",
                        'data-qa': `${erpType}-save-close-button`,
                        'variant': "contained",
                        'children': t('saveAndClose'),
                        'disabled': hasError
                    }
                ]
            }, '*');
        } else {
            window.parent.postMessage({
                message: IFrameModalMessageIds.footerButtons, value: [
                    {'variant': "hidden", 'disabled': "true"}
                ]
            }, '*');
        }
    }, [showFooterButtons, erpType, t, hasError]);

    const closeReviewModal = () => {
        setReviewModalOpened(false);
        setShowFooterButtons(true);
        if (isInsideEmbark) {
            window.parent.postMessage({message: IFrameModalMessageIds.headerShowClose, value: true}, '*');
        }
    };

    const navigateToReviewPage = () => {
        closeReviewModal();
        navigate(basePath + reviewRoute, {
            state: {
                clickCheckYourConfigurationButton: true,
            }
        });
    };

    const closeCancelModal = () => {
        setCancelModalOpened(false);
        setShowFooterButtons(true);
        if (isInsideEmbark) {
            window.parent.postMessage({message: IFrameModalMessageIds.headerShowClose, value: true}, '*');
        }
    };

    const reviewConfigurationModal = () => {
        if (!reviewModalOpened) {
            return null;
        }
        const modalHeader = <ModalHeader showClose title={t('review.configuration.modal.header')}
                                         onCloseClicked={() => closeReviewModal()}
                                         slotProps={{iconButton: {customAttributes: {'data-qa': 'review-configuration-modal-close-button'}}}}/>
        const modalFooter = (
            <ModalFooter>
                <ButtonContainer alignButtons="right">
                    <Button
                        data-qa="review-configuration-modal-confirm"
                        color="info"
                        id="review-configuration-modal-confirm"
                        variant="outlined"
                        onClick={() => {
                            setSuccessfulSyncConfiguration?.(false);
                            saveSettings(false);
                        }}>
                        {t('review.configuration.modal.later')}
                    </Button>
                    <Button
                        data-qa="review-configuration-modal-review.changes"
                        id="review-configuration-modal-review.changes"
                        onClick={() => navigateToReviewPage()}>
                        {t('review.configuration.modal.review.changes')}
                    </Button>
                </ButtonContainer>
            </ModalFooter>
        );

        return (
            <ConfirmationModal modalOpened={reviewModalOpened}
                               modalHeader={modalHeader}
                               modalFooter={modalFooter}
                               description={t('review.configuration.modal.description')}
                               dataQA={"review-configuration"}
            />
        );
    }

    const cancelConfigurationModal = () => {
        if (!cancelModalOpened) {
            return null;
        }
        const modalHeader = <ModalHeader showClose title={t('cancel.configuration.modal.header')}
                                         onCloseClicked={() => closeCancelModal()}
                                         slotProps={{iconButton: {customAttributes: {'data-qa': 'cancel-configuration-modal-close-button'}}}}/>
        const modalFooter = (
            <ModalFooter>
                <ButtonContainer alignButtons="right">
                    <Button
                        data-qa="cancel-configuration-modal-cancel"
                        id="cancel-configuration-modal-cancel"
                        onClick={() => closeCancelModal()}
                        variant="outlined">
                        {t('cancel.configuration.modal.cancel')}
                    </Button>
                    <Button
                        data-qa="cancel-configuration-modal-confirm"
                        color="info"
                        id="cancel-configuration-modal-confirm"
                        onClick={() => {
                            closeModal()
                        }}>
                        {t('cancel.configuration.modal.confirm')}
                    </Button>
                </ButtonContainer>
            </ModalFooter>
        );

        return (
            <ConfirmationModal modalOpened={cancelModalOpened}
                               modalHeader={modalHeader}
                               modalFooter={modalFooter}
                               warningIcon
                               description={t('cancel.configuration.modal.description')}
                               dataQA={"cancel-configuration"}
            />
        );
    }


    return (
        <>
            <Snackbar open={open} onClose={() => setOpen(false)}>
                <Alert severity="error" onClose={() => setOpen(false)}>
                    {errorMessage}
                </Alert>
            </Snackbar>
            {<Box className="FooterBar" data-qa="footer-bar" sx={{display: isInsideEmbark ? 'none' : 'unset'}}>
                <ButtonContainer alignButtons="right">
                    {showFooterButtons &&
                        <><Button id="cancel"
                                  data-qa="cancel-button"
                                  variant="outlined"
                                  ref={cancelRef}
                                  onClick={cancel}>
                            {t('cancel')}
                        </Button>
                            <Button id="saveAndClose"
                                    data-qa="save-close-button"
                                    ref={saveAndCloseRef}
                                    onClick={saveAndClose}>
                                {t('saveAndClose')}
                            </Button>
                        </>
                    }
                </ButtonContainer>
            </Box>
            }
            {reviewConfigurationModal()}
            {cancelConfigurationModal()}
        </>
    )
};